package features.notifications.handlers

import AppState
import Router
import features.notifications.data.NotificationRepository
import core.firebase.FirebaseService

/**
 * Handlers for notification actions
 */

/**
 * Mark a notification as read
 */
fun markNotificationAsRead(notificationId: String) {
    val userId = AppState.currentUser?.id ?: return
    
    console.log("📖 Marking notification as read:", notificationId)
    
    NotificationRepository.markAsRead(userId, notificationId)
        .then {
            console.log("✅ Notification marked as read")
            
            // Update local state
            val notification = AppState.notifications.find { it.id == notificationId }
            if (notification != null) {
                val index = AppState.notifications.indexOf(notification)
                AppState.notifications[index] = notification.copy(isRead = true)
                
                // Update unread count
                AppState.unreadNotificationCount = AppState.notifications.count { !it.isRead }
                
                // Re-render to update UI
                Router.render()
            }
        }
        .catch { error ->
            console.error("❌ Error marking notification as read:", error)
        }
}

/**
 * Mark all notifications as read
 */
fun markAllNotificationsAsRead() {
    val userId = AppState.currentUser?.id ?: return
    
    console.log("📖 Marking all notifications as read")
    
    NotificationRepository.markAllAsRead(userId)
        .then {
            console.log("✅ All notifications marked as read")
            
            // Update local state
            AppState.notifications = AppState.notifications.map { it.copy(isRead = true) }.toMutableList()
            AppState.unreadNotificationCount = 0
            
            // Re-render to update UI
            Router.render()
        }
        .catch { error ->
            console.error("❌ Error marking all notifications as read:", error)
        }
}

/**
 * Load notifications for current user
 */
fun loadNotifications() {
    val userId = AppState.currentUser?.id ?: return
    
    console.log("📥 Loading notifications for user:", userId)
    
    NotificationRepository.getUserNotifications(userId)
        .then { notifications ->
            console.log("✅ Loaded ${notifications.size} notifications")
            
            // Sort by createdAt (newest first)
            val sortedNotifications = notifications.sortedByDescending { it.createdAt }
            
            AppState.notifications = sortedNotifications.toMutableList()
            AppState.unreadNotificationCount = notifications.count { !it.isRead }
            
            console.log("📊 Unread count:", AppState.unreadNotificationCount)
            
            // Re-render to update UI
            Router.render()
        }
        .catch { error ->
            console.error("❌ Error loading notifications:", error)
        }
}

/**
 * Setup real-time listener for notifications
 */
fun setupNotificationListener() {
    val userId = AppState.currentUser?.id ?: return
    
    console.log("👂 Setting up notification listener for user:", userId)
    
    try {
        val query = FirebaseService.db
            .collection("users")
            .doc(userId)
            .collection("notifications")
        
        // Use dynamic call for onSnapshot
        query.asDynamic().onSnapshot({ snapshot: dynamic ->
            console.log("🔔 Notification snapshot received, docs count:", snapshot.size)
            
            val notifications = mutableListOf<features.notifications.domain.Notification>()
            
            snapshot.forEach { doc: dynamic ->
                try {
                    val rawData: dynamic = doc.data()
                    console.log("📄 Processing notification doc:", doc.id, rawData)
                    
                    val createdAt = formatTimestamp(rawData.createdAt)
                    console.log("⏰ Formatted timestamp:", createdAt)
                    
                    val notification = features.notifications.domain.Notification(
                        id = doc.id,
                        userId = rawData.userId as? String ?: "",
                        type = rawData.type as? String ?: "",
                        title = rawData.title as? String ?: "",
                        message = rawData.message as? String ?: "",
                        isRead = rawData.isRead as? Boolean ?: false,
                        createdAt = createdAt,
                        relatedId = rawData.relatedId as? String
                    )
                    notifications.add(notification)
                    console.log("✅ Notification added:", notification.id, notification.title)
                } catch (e: Throwable) {
                    console.error("❌ Error processing notification doc:", doc.id, e)
                    console.error("Stack trace:", e.stackTraceToString())
                }
            }
            
            console.log("📋 Total notifications before sorting:", notifications.size)
            
            // Sort by createdAt (newest first) - handle empty timestamps
            val sortedNotifications = try {
                notifications.sortedByDescending { 
                    if (it.createdAt.isEmpty()) "0" else it.createdAt 
                }
            } catch (e: Throwable) {
                console.error("❌ Error sorting notifications:", e)
                notifications // Return unsorted if sorting fails
            }
            
            console.log("📋 Total notifications after sorting:", sortedNotifications.size)
            
            AppState.notifications = sortedNotifications.toMutableList()
            AppState.unreadNotificationCount = notifications.count { !it.isRead }
            
            console.log("✅ Notifications updated: ${notifications.size} total, ${AppState.unreadNotificationCount} unread")
            console.log("📊 AppState.notifications size:", AppState.notifications.size)
            
            // Re-render to update UI
            Router.render()
        })
    } catch (e: Throwable) {
        console.error("❌ Error setting up notification listener:", e)
        console.error("Stack trace:", e.stackTraceToString())
    }
}

/**
 * Format Firestore timestamp to ISO string
 */
private fun formatTimestamp(timestamp: dynamic): String {
    return try {
        if (timestamp == null || timestamp == js("undefined")) {
            ""
        } else {
            val date: dynamic = timestamp.toDate()
            date.toISOString()
        }
    } catch (e: Throwable) {
        ""
    }
}
